import { Classes as CoreClasses } from "@blueprintjs/core";
import { darken, rgba } from "polished";
import React from "react";
import styled, { css } from "styled-components";

import { STATIC_ICON } from "../components/icons/staticIcons.js";

import { htmlSelectStyles } from "./HexHTMLSelect";
import { inputStyleRules } from "./HexInput";
import { TimePicker } from "./timepicker/TimePicker";
import { Classes } from "./timepicker/timepickerClasses";
import { TimePickerProps } from "./timepicker/timePickerProps.js";
import { TimezoneSelect, TimezoneSelectProps } from "./timezone/TimezoneSelect";

export const HexTimePicker = styled(
  TimePicker as React.ComponentType<TimePickerProps>,
)<{
  $small?: boolean;
  $defaultSize?: boolean;
}>`
  .${Classes.TIMEPICKER_INPUT_ROW} {
    ${inputStyleRules}

    .${Classes.TIMEPICKER_INPUT} {
      background-color: transparent !important;
      color: ${({ theme }) => theme.fontColor.DEFAULT};
      box-shadow: none !important;
      text-align: right;

      width: 32px;
      padding-right: 8px;
    }
    .${Classes.TIMEPICKER_DIVIDER_TEXT} {
      width: 4px;
    }
  }

  /* Hide the bp svg, and then add our own svg code as a background image
    AM/PM picker select icon (or, for the small variant, hide it entirely)
    */
  .${CoreClasses.ICON} {
    background-color: ${({ theme }) => theme.iconColor};
    background-image: none;
    mask-image: ${`url('data:image/svg+xml,${STATIC_ICON.ChevronExpand}')`};

    display: ${({ $small }) => ($small ? "none" : "block")};
    svg {
      visibility: hidden;
    }
  }

  .${CoreClasses.HTML_SELECT} {
    &&&&&&&&&&&&&&&&&&&&&& {
      font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
    }
    ${htmlSelectStyles}

    &.${Classes.TIMEPICKER_AMPM_SELECT} select {
      font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
    }
  }

  ${({ $defaultSize, $small, theme }) => css`
    ${$small &&
    `
      .${Classes.TIMEPICKER_INPUT_ROW},
      .${Classes.TIMEPICKER_INPUT} {
        height: 24px;
        min-height: 24px;
        line-height: 24px;
      }

      .${Classes.TIMEPICKER_INPUT} {
        vertical-align: top;
      }

      .${Classes.TIMEPICKER_DIVIDER_TEXT} {
        margin-top: -1px;
      }
    `}

    ${$defaultSize &&
    `
      font-size: ${theme.fontSize.DEFAULT};

      .${Classes.TIMEPICKER_INPUT_ROW},
      .${Classes.TIMEPICKER_INPUT} {
        height: 30px;
        min-height: 30px;
        line-height: 30px;
      }

      .${Classes.TIMEPICKER_INPUT} {
        vertical-align: top;
      }
    `}
  `}
`;

export interface HexTimezonePickerProps extends TimezoneSelectProps {
  className?: string;
  minimal?: boolean;
}

export const HexTimezonePicker: React.ComponentType<HexTimezonePickerProps> =
  React.memo(function HexTimezonePicker({ className, ...props }) {
    return (
      <StyledTimezonePicker
        className={className}
        {...props}
        popoverProps={{
          usePortal: true,
          ...props.popoverProps,
        }}
      />
    );
  });

const StyledTimezonePicker = styled(
  TimezoneSelect as React.ComponentType<TimezoneSelectProps>,
)`
  .${CoreClasses.BUTTON} {
    ${({ theme }) => css`
      color: ${theme.button.default.none.fontColor};

      background-color: ${theme.button.default.none.backgroundColor};
      box-shadow: inset 0 0 0 1px ${theme.button.default.none.borderColor};

      &:not(.${CoreClasses.DISABLED}) {
        &:hover {
          background-color: ${theme.hoverColor};
        }

        &:active {
          background-color: ${theme.activeColor};
        }

        &.${CoreClasses.ACTIVE} {
          background-color: ${darken(0.03, theme.activeColor)};
          box-shadow: inset 0 0 0 1px ${darken(0.2, theme.activeColor)};
        }
      }
    `}
  }
  .${CoreClasses.SELECTED}, .${CoreClasses.SELECTED}:hover {
    background-color: ${({ theme }) => rgba(theme.intent.PRIMARY, 0.2)};
    color: ${({ theme }) => theme.intent.PRIMARY};
  }

  /* Hide the bp svg, and then add our own svg code as a background image
    AM/PM picker select icon
    */
  .${CoreClasses.ICON} {
    background-color: ${({ theme }) => theme.iconColor};
    background-image: none;
    mask-image: ${`url('data:image/svg+xml,${STATIC_ICON.SingleChevronDown}')`};

    svg {
      visibility: hidden;
    }
  }
`;
